import { datadogRum } from '@datadog/browser-rum';

/**
 * Patterns we drop in `beforeSend` because they are non-actionable noise
 * (third-party scripts, browser extensions, scanner bots, or SDK retry-storm
 * logging) that buries real bugs in the Datadog RUM error feed.
 *
 * Counts below are 14-day occurrences observed before the filter landed
 * (web-www, application `web-www`, real-user sessions).
 *
 * Match is against `event.error.message` or `event.error.stack`. We keep any
 * event whose stack originates from `_next/static/` chunks, since those are
 * our own code — see `isOriginatedInOurBundle` below.
 */
const NOISY_ERROR_PATTERNS: readonly RegExp[] = [
	// Cross-origin scripts without CORS — no stack, no context, not fixable
	// from our side (~32k/14d).
	/^Uncaught "Script error\.?"?$/,
	// Amplitude SDK logger noise from benign client-side transport failures
	// (network blips, adblock, offline) — the fetch-rejection wording is
	// browser-dependent, hence the family match. Other Amplitude Logger errors
	// (ingestion failures, config/validation, invalid key) are real health
	// signals as Amplitude becomes the primary analytics pipeline, so those
	// are intentionally kept rather than muted.
	/^Amplitude Logger \[Error\]: .*(Failed to fetch|Load failed|NetworkError|network connection was lost|Internet connection appears to be offline|timed out|Timeout|net::ERR)/i,
	// Outlook safelink scanner artefact (~1.8k/14d).
	/Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
	// Resource-load errors surfaced with only `{"isTrusted":true}` payload —
	// no message or stack to act on (~1.3k/14d).
	/^Uncaught \{"isTrusted":true\}$/,
	// Mobile Safari browser-extension injection (~226/14d, one session).
	/Can't find variable: EmptyRanges/,
	// Generic browser-extension messaging failure — not our code (~48/14d).
	/Invalid call to runtime\.sendMessage\(\)\. Tab not found\./,
	// iOS autofill extension hook (~21/14d).
	/Can't find variable: _AutofillCallbackHandler/,
	// GTM-injected Google Publisher Tag fragments that reference `googletag`
	// before GPT has loaded (same race, also fired from inside gtm.js) (~700/14d).
	/googletag is not defined/,
	// Segment's analytics.js loader giving up on fetching its own script — a
	// third-party CDN/network failure, not our code.
	/\[analytics\.js\] Failed to load Analytics\.js/,
	// Segment SDK's internal performance-metrics beacon failing to send — its own
	// telemetry, nothing actionable on our side.
	/Error sending segment performance metrics/,
	// Third-party analytics autocapture click handler choking on an SVG element,
	// whose `className` is an `SVGAnimatedString` (no `.slice`). Confirmed NOT in
	// our source — the handler lives in a vendor autocapture bundle. Anchored on
	// the handler prefix so it can't suppress a first-party `.slice` TypeError
	// (~30/7d).
	/Error handling click event: TypeError: .*className\.slice is not a function/,
];

// Stacks containing one of these substrings originate in a third-party script,
// not our code. Matched against `error.stack` only, where the message itself is
// too generic to match on safely:
//   - Magellan AI's pixel (~63/14d).
//   - Segment's analytics-next chunk loader. A `ChunkLoadError` for one of these
//     bundles is Segment failing to fetch its own code; we match the resource
//     host rather than the generic `ChunkLoadError` message so our own
//     `/_next/static/` chunk-load failures (real bugs) are never suppressed.
const NOISY_STACK_SUBSTRINGS: readonly string[] = [
	'cdn.mgln.ai/pixel.min.js',
	'cdn.segment.com/analytics-next/bundles/',
];

// Anything whose stack hits a Next.js static chunk is from our bundle.
// We keep these even if the message superficially matches a noise pattern —
// real bugs in our code must not be silently dropped.
const isOriginatedInOurBundle = (stack: string | undefined): boolean =>
	typeof stack === 'string' && stack.includes('/_next/static/');

const isNoisyError = (message: string | undefined, stack: string | undefined): boolean => {
	if (isOriginatedInOurBundle(stack)) return false;

	if (typeof message === 'string') {
		for (const pattern of NOISY_ERROR_PATTERNS) {
			if (pattern.test(message)) return true;
		}
	}
	if (typeof stack === 'string') {
		for (const needle of NOISY_STACK_SUBSTRINGS) {
			if (stack.includes(needle)) return true;
		}
	}
	return false;
};

export const initDatadogRum = (): void => {
	datadogRum.init({
		applicationId: '2eda852e-f548-4799-b949-c4dc88df9390',
		clientToken: 'pub765f9f1e73096b3302d050f96722d77a',
		site: 'datadoghq.com',
		service: 'web-www',
		version: process.env.BUILD_ID,
		env: process.env.NEXT_PUBLIC_LOG_ENV,
		sessionSampleRate: 100,
		traceSampleRate: 20,
		trackUserInteractions: true,
		// TTFB (time-to-first-byte) is already collected by the SDK and indexed
		// by Datadog under the attribute `@view.first_byte` (duration in ns).
		// Note: it is NOT exposed as `@view.first_byte_time` — that attribute
		// name does not exist in our org and queries against it will return
		// `dropped_computes`.
		beforeSend: event => {
			// Password-reset tokens in the URL must not be sent to Datadog RUM.
			// Once we have fully migrated to Auth0 this should no longer be
			// reachable and the redaction can be removed.
			event.view.url = event.view.url.replace(/\/password-reset\/.+/, '/password-reset/REDACTED');

			// Content-Security-Policy-Report-Only (and other Reporting API) violations
			// surface as error events with source "report" — they are policy
			// violations, not JS errors, and at our current volume they dominate the
			// error-rate metric and bury real JS errors. The violations themselves
			// are still captured server-side by the CSP report sink, so nothing is
			// lost by dropping them here.
			if (event.type === 'error' && event.error?.source === 'report') {
				return false;
			}

			// Drop error events matching known third-party / extension / scanner
			// noise patterns. See `NOISY_ERROR_PATTERNS` / `NOISY_STACK_SUBSTRINGS`
			// above for the catalogue and rationale.
			if (event.type === 'error' && isNoisyError(event.error?.message, event.error?.stack)) {
				return false;
			}

			// true means the event should not be discarded.
			return true;
		},
	});
};
